import merge from "lodash.merge";
import { render } from "react-dom";
import { Provider } from "react-redux";

import { store } from "./store/store";
import { WebchatWidget } from "./components/WebchatWidget";
import { sessionDataHandler } from "./sessionDataHandler";
import { initConfig, initPreEngagementFormState } from "./store/actions/initActions";
import { ConfigState, WebchatEventListenerCallback } from "./store/definitions";
import { initLogger } from "./logger";
import { addWebchatListener } from "./store/actions/listeners/webchatListener";
import { changeExpandedStatus } from "./store/actions/genericActions";

const getDomain = () => {
    if (
        window.location.hostname !== "localhost" &&
        document.currentScript &&
        (document.currentScript as HTMLScriptElement).src
    ) {
        const uri = new URL((document.currentScript as HTMLScriptElement).src);
        return uri.origin;
    }
    return process.env.REACT_APP_LOCAL_SERVER_URL;
};

const defaultConfig: ConfigState = {
    serverUrl: getDomain(),
    theme: {
        isLight: true
    },
    fileAttachment: {
        enabled: false,
        maxFileSize: 16777216, // 16 MB
        acceptedExtensions: ["jpg", "jpeg", "png", "amr", "mp3", "mp4", "pdf", "txt"]
    },
    transcript: {
        downloadEnabled: false,
        emailEnabled: false,
        emailSubject: (agentNames) => {
            let subject = "Transcript of your chat";
            if (agentNames.length > 0) {
                subject = subject.concat(` with ${agentNames[0]}`);
                agentNames.slice(1).forEach((name) => (subject = subject.concat(` and ${name}`)));
            }
            return subject;
        },
        emailContent: (customerName, transcript) => {
            return `<div><h1 style="text-align:center;">Chat Transcript</h1><p>Hello ${customerName},<br><br>Please see below your transcript, with any associated files attached, as requested.<br><br>${transcript}</p></div>`;
        }
    }
};

const Webchat = {
    manager: {
        store
    },
    Actions: {
        on: (event: string, listenerCb: WebchatEventListenerCallback) =>
            store.dispatch(addWebchatListener(event, listenerCb)),
        changeExpandedStatus: (expanded: boolean) => store.dispatch(changeExpandedStatus({ expanded }))
    }
};

const initWebchat = async (config: ConfigState) => {
    const mergedConfig = merge({}, defaultConfig, config);
    sessionDataHandler.setEndpoint(mergedConfig.serverUrl);
    store.dispatch(initConfig(mergedConfig));
    store.dispatch(changeExpandedStatus({ expanded: Boolean(config.expanded) }));
    if (config.preEngagementConfig) {
        store.dispatch(initPreEngagementFormState(config.preEngagementConfig, config.context));
    }
    initLogger();
    const rootElement = document.getElementById("twilio-webchat-widget-root");

    render(
        <Provider store={store}>
            <WebchatWidget />
        </Provider>,
        rootElement
    );

    if (window.Cypress) {
        window.store = store;
    }
    return Webchat;
};

declare global {
    interface Window {
        TwilioChat: {
            initWebchat: (config: ConfigState) => void;
        };
        Cypress: Cypress.Cypress;
        store: typeof store;
    }
}

// Expose `initWebchat` function to window object
Object.assign(window, {
    TwilioChat: {
        initWebchat,
        Webchat
    }
});
